import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  EntPageShell, cardStyle, panelHeader, goldBtn, ghostBtn, ENT_COLOR,
  SeverityPill, Empty, ErrorMsg,
} from "./_shell";
import {
  enterpriseApi, performanceApi, wearableApi, portalApi, reportsEntApi,
  tasksApi, integrationApi, auditEntApi,
} from "../../lib/enterpriseApi";
import { api as apiAxios, auth } from "../../lib/api";

const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString() : "—"; } catch (_) { return iso; } };

// ==================================================== Enterprise Hub
const HUB_TILES = [
  { path: "/enterprise/messaging",       title: "Messaging",       icon: "✉",  testid: "hub-tile-messaging",       deep: true },
  { path: "/enterprise/scheduling",      title: "Scheduling",      icon: "▦",  testid: "hub-tile-scheduling",      deep: true },
  { path: "/enterprise/medical",         title: "Medical Workflow",icon: "✚",  testid: "hub-tile-medical",         deep: true },
  { path: "/enterprise/compliance",      title: "Compliance",      icon: "§",  testid: "hub-tile-compliance",      deep: true },
  { path: "/enterprise/documents",       title: "Documents",       icon: "▣",  testid: "hub-tile-documents",       deep: true },
  { path: "/enterprise/alerts",          title: "Alert Center",    icon: "⚠",  testid: "hub-tile-alerts",          deep: true },
  { path: "/enterprise/performance",     title: "Training & Performance", icon: "↗", testid: "hub-tile-performance" },
  { path: "/enterprise/wearables",       title: "Wearable Devices",icon: "◉",  testid: "hub-tile-wearables" },
  { path: "/enterprise/portal",          title: "Athlete & Parent Portal", icon: "◈", testid: "hub-tile-portal" },
  { path: "/enterprise/reports",         title: "Reports",         icon: "▤",  testid: "hub-tile-reports" },
  { path: "/enterprise/workflows",       title: "Staff Workflows", icon: "✓",  testid: "hub-tile-workflows" },
  { path: "/enterprise/integrations",    title: "Integration Gateway", icon: "↔", testid: "hub-tile-integrations" },
  { path: "/enterprise/audit",           title: "Audit Log Viewer",icon: "🔒", testid: "hub-tile-audit" },
  { path: "/enterprise/mobile",          title: "Mobile / Offline",icon: "▢",  testid: "hub-tile-mobile" },
];

export const EnterpriseHub = () => {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [params] = useSearchParams();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!auth.token()) { setError("Sign in to view enterprise overview."); return; }
    enterpriseApi.overview().then(setOverview).catch(() => setError("Could not load overview."));
  }, []);

  const orgParam = (params.get("org") || "").toLowerCase();

  return (
    <EntPageShell title="Enterprise Operations" testId="enterprise-hub"
      lede="14 modules built on AuditIQ Sports Intelligence. Every module is JWT + RBAC enforced, HIPAA/FERPA logged where applicable, and integrated with the HealthIQ engine, Crawl Engine, and 3-tier fallback chain."
      headerExtra={
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Link to="/security" data-testid="hub-security-badge"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px",
              border: "1px solid rgba(201,168,76,0.55)", color: "#c9a84c", borderRadius: 4,
              fontFamily: "Cinzel, Georgia, serif", fontSize: 10, letterSpacing: 1.2,
              textTransform: "uppercase", fontWeight: 600, textDecoration: "none" }}>
            ✓ Security &amp; Compliance
          </Link>
          <button data-testid="hub-procurement-cta" style={goldBtn()} onClick={() => setShowModal(true)}>
            Schedule procurement demo
          </button>
        </div>
      }>
      {error && <ErrorMsg message={error} />}
      {overview?.modules && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 22 }} data-testid="hub-counts-row">
          {Object.entries(overview.modules).map(([k, v]) => (
            <div key={k} data-testid={`hub-count-${k}`} style={{ ...cardStyle, padding: 12, borderWidth: 1, borderStyle: "solid", borderColor: "rgba(201,168,76,0.5)" }}>
              <div style={{ fontSize: 22, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", fontWeight: 700 }}>{v}</div>
              <div style={{ fontSize: 9, color: "rgba(244,239,230,0.65)", letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "Cinzel, Georgia, serif", marginTop: 2 }}>{k}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
        {HUB_TILES.map((t) => (
          <Link key={t.path} to={t.path} data-testid={t.testid}
            style={{ ...cardStyle, textDecoration: "none", color: ENT_COLOR.text, display: "block", transition: "transform 120ms ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 22, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif" }}>{t.icon}</div>
              <div style={{ fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.5, color: ENT_COLOR.gold, fontSize: 13, textTransform: "uppercase" }}>{t.title}</div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(244,239,230,0.65)", marginTop: 6 }}>
              {t.deep ? "Full UI · live data" : "Scaffold UI · API live"}
            </div>
          </Link>
        ))}
      </div>
      {showModal && <ProcurementModal orgParam={orgParam} onClose={() => setShowModal(false)} />}
    </EntPageShell>
  );
};

// ==================================================== Procurement Demo Modal
const ORG_OPTIONS = [
  { id: "uf",          label: "University of Florida Gators" },
  { id: "fsu",         label: "Florida State University Seminoles" },
  { id: "um",          label: "University of Miami Hurricanes" },
  { id: "drphillips",  label: "Dr. Phillips High School Panthers" },
  { id: "other",       label: "Other institution" },
];

const ProcurementModal = ({ orgParam, onClose }) => {
  const initialOrg = ORG_OPTIONS.find((o) => o.id === orgParam) || ORG_OPTIONS[4];
  const [form, setForm] = useState({
    org: initialOrg.id,
    institution: initialOrg.label,
    contact_name: "",
    contact_email: "",
    contact_title: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (busy) return;
    if (!form.contact_name.trim() || !form.contact_email.trim()) {
      setError("Name and email are required."); return;
    }
    setBusy(true); setError(null);
    try {
      const r = await apiAxios.post("/procurement/schedule-demo", form).then((x) => x.data);
      setResult(r);
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="procurement-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(13,31,60,0.85)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 520, background: ENT_COLOR.card, border: `2px double ${ENT_COLOR.gold}`, borderRadius: 8, padding: 28, color: ENT_COLOR.text, fontFamily: "Merriweather, Georgia, serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <h2 style={{ fontFamily: "Cinzel, Trajan Pro, Georgia, serif", color: ENT_COLOR.gold, fontSize: 22, letterSpacing: 2, textTransform: "uppercase", margin: 0 }} data-testid="procurement-modal-title">
            Schedule procurement demo
          </h2>
          <button data-testid="procurement-modal-close" onClick={onClose} style={{ background: "transparent", border: "none", color: ENT_COLOR.gold, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        {result ? (
          <div data-testid="procurement-result">
            <div style={{ background: "rgba(46,204,113,0.14)", border: `1px solid ${ENT_COLOR.green}`, color: ENT_COLOR.green, padding: 12, borderRadius: 4, fontSize: 12, marginBottom: 14 }}>
              {result.message}
            </div>
            <div style={{ fontSize: 11, color: "rgba(244,239,230,0.7)", marginBottom: 16, lineHeight: 1.6 }}>
              Lead id: <span style={{ fontFamily: "ui-monospace, Menlo, monospace", color: ENT_COLOR.gold }}>{result.lead_id}</span><br/>
              Buyer email: {result.buyer_confirmation?.provider} provider · sales notification: {result.sales_notification?.provider} provider
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <a data-testid="procurement-pdf-link" href={result.demo_pdf_url} target="_blank" rel="noreferrer" style={{ ...goldBtn(), textDecoration: "none", display: "inline-block" }}>
                Download demo PDF
              </a>
              <button data-testid="procurement-done-btn" style={ghostBtn()} onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 12, color: "rgba(244,239,230,0.7)", marginBottom: 18, lineHeight: 1.6 }}>
              Tell us where you're evaluating. We'll email your demo summary PDF and notify the institutional sales team.
            </p>
            {error && <ErrorMsg message={typeof error === "string" ? error : "Submission failed."} />}
            <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>Institution</label>
            <select data-testid="procurement-org-select" value={form.org}
              onChange={(e) => {
                const opt = ORG_OPTIONS.find((o) => o.id === e.target.value);
                setForm({ ...form, org: e.target.value, institution: opt?.label || "" });
              }}
              style={{ width: "100%", padding: 10, marginTop: 4, marginBottom: 12, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }}>
              {ORG_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            {form.org === "other" && (
              <input data-testid="procurement-institution-input" placeholder="Institution name"
                value={form.institution}
                onChange={(e) => setForm({ ...form, institution: e.target.value })}
                style={{ width: "100%", padding: 10, marginBottom: 12, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }} />
            )}
            {[
              { k: "contact_name",  label: "Your name *",   placeholder: "Jane Doe" },
              { k: "contact_title", label: "Title",         placeholder: "Director of Athletics Procurement" },
              { k: "contact_email", label: "Email *",       placeholder: "jane@institution.edu" },
            ].map((f) => (
              <div key={f.k} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>{f.label}</label>
                <input data-testid={`procurement-${f.k}`} placeholder={f.placeholder} value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  style={{ width: "100%", padding: 10, marginTop: 4, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }} />
              </div>
            ))}
            <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>Notes (optional)</label>
            <textarea data-testid="procurement-notes" rows={3} value={form.notes}
              placeholder="Sports of interest, timeline, decision committee…"
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              style={{ width: "100%", padding: 10, marginTop: 4, marginBottom: 16, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4, fontFamily: "inherit" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button data-testid="procurement-cancel" style={ghostBtn()} onClick={onClose}>Cancel</button>
              <button data-testid="procurement-submit" style={goldBtn()} onClick={submit} disabled={busy}>
                {busy ? "Sending…" : "Send & email me the PDF"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================================================== §4 TrainingPerformance scaffold
export const TrainingPerformanceScaffold = () => {
  const ATHLETE = "demo-uf-football-01";
  const [trend, setTrend] = useState([]);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auth.token()) { setError("Sign in to view performance."); return; }
    Promise.all([performanceApi.trend(ATHLETE), performanceApi.history(ATHLETE)])
      .then(([t, h]) => { setTrend(t.trend || []); setHistory(h.history || []); })
      .catch(() => setError("Could not load performance data."));
  }, []);

  const max = Math.max(...trend.map((d) => d.training_load), 100);

  return (
    <EntPageShell title="Training &amp; Performance" testId="performance-scaffold"
      lede="Workout plans, strength programs, conditioning tracking, training-load monitoring. Feeds Health IQ availability risk recalc.">
      {error && <ErrorMsg message={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="performance-trend-card">
          <div style={panelHeader}>30-day training load — {ATHLETE}</div>
          {trend.length === 0 ? <Empty note="No trend data yet." /> :
            <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 100, marginTop: 4 }}>
              {trend.map((d) => (
                <div key={d.day} title={`Day ${d.day}: load ${d.training_load}`}
                  style={{ flex: 1, height: `${(d.training_load / max) * 100}%`, background: d.training_load > 80 ? ENT_COLOR.red : ENT_COLOR.gold, borderRadius: "2px 2px 0 0", minHeight: 3 }} />
              ))}
            </div>}
          <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)", marginTop: 6 }}>
            Bars exceeding 80 (red) flag fatigue review.
          </div>
        </div>
        <div style={cardStyle} data-testid="performance-history-card">
          <div style={panelHeader}>Recent workouts ({history.length})</div>
          {history.length === 0 ? <Empty note="No workouts logged yet — POST /api/performance/update." /> :
            history.slice(-8).reverse().map((w) => (
              <div key={w.id} style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "6px 0", fontSize: 12 }}>
                {w.workout_type} · load {w.training_load} · {fmtDate(w.recorded_at)}
              </div>
            ))}
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §5 Wearables scaffold
export const WearablesScaffold = () => {
  const ATHLETE = "demo-uf-football-01";
  const [devices, setDevices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await wearableApi.status();
      setDevices(r.devices || []);
      setProviders(r.supported_providers || []);
    } catch (e) { setError("Sign in to view wearables."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to view wearables."); }, []);

  const connect = async (device_type) => {
    setBusy(true);
    try {
      await wearableApi.connect({ athlete_id: ATHLETE, device_type, device_id: `${device_type}-${Date.now()}` });
      await refresh();
    } catch (e) { setError("Connect failed."); }
    finally { setBusy(false); }
  };

  const sync = async (device_id) => {
    try { await wearableApi.sync(device_id); await refresh(); }
    catch (e) { setError("Sync failed."); }
  };

  return (
    <EntPageShell title="Wearable Devices" testId="wearables-scaffold"
      lede="Apple HealthKit · Garmin · Fitbit · WHOOP · Catapult · Polar — OAuth-backed in production, demo-mode tokens here. Sync feeds Health IQ recovery + workload.">
      {error && <ErrorMsg message={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="wearables-devices-card">
          <div style={panelHeader}>Connected devices ({devices.length})</div>
          {devices.length === 0 ? <Empty note="No devices yet — connect from the right panel." /> :
            devices.map((d) => (
              <div key={d.device_id || d.device_type} data-testid="wearables-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13 }}>{d.device_type}</div>
                  <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)" }}>last sync {fmtDate(d.last_sync)} · battery {d.battery_level}%</div>
                </div>
                <button data-testid="wearables-sync-btn" style={ghostBtn(true)} onClick={() => sync(d.device_id)}>Sync</button>
              </div>
            ))}
        </div>
        <div style={cardStyle} data-testid="wearables-connect-card">
          <div style={panelHeader}>Connect new device</div>
          {providers.map((p) => (
            <button key={p} data-testid={`wearables-connect-${p}`} style={{ ...ghostBtn(), display: "block", width: "100%", marginBottom: 6 }} onClick={() => connect(p)} disabled={busy}>
              {p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </button>
          ))}
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §8 Portal scaffold
export const PortalScaffold = () => {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auth.token()) { setError("Sign in (athlete or parent) to view portal."); return; }
    Promise.all([portalApi.dashboard(), portalApi.healthSummary()])
      .then(([d, h]) => { setData(d); setHealth(h); })
      .catch(() => setError("Portal data unavailable for this role."));
  }, []);

  return (
    <EntPageShell title="Athlete &amp; Parent Portal" testId="portal-scaffold"
      lede="Mobile-friendly portal for athletes and verified parents/guardians. Athletes see Health IQ details; parents see fuzzed availability summaries only.">
      {error && <ErrorMsg message={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="portal-dashboard-card">
          <div style={panelHeader}>My dashboard</div>
          {data ? (
            data.verified === false ? (
              <Empty note={data.message} testid="portal-not-verified" />
            ) : (
              <>
                <div style={{ fontSize: 13, marginBottom: 8 }}>Athlete: <span style={{ color: ENT_COLOR.gold }}>{data.athlete_id}</span></div>
                <div style={{ fontSize: 11, color: "rgba(244,239,230,0.55)" }}>
                  {data.schedule?.length || 0} events upcoming · {data.injuries?.length || 0} injury report(s) on file
                </div>
              </>
            )
          ) : <Empty />}
        </div>
        <div style={cardStyle} data-testid="portal-health-card">
          <div style={panelHeader}>Availability</div>
          {health ? (
            <div>
              <div style={{ fontSize: 22, fontFamily: "Cinzel, Georgia, serif", color: health.availability_risk === "elevated" ? ENT_COLOR.red : ENT_COLOR.green, fontWeight: 700 }}>
                {(health.availability_risk || health.availability || "clear").toUpperCase()}
              </div>
              {health.note && <div style={{ fontSize: 11, color: "rgba(244,239,230,0.55)", marginTop: 6 }}>{health.note}</div>}
              {health.reason && <div style={{ fontSize: 11, color: "rgba(244,239,230,0.55)", marginTop: 6 }}>Reason: {health.reason}</div>}
            </div>
          ) : <Empty />}
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §10 Reports scaffold
export const ReportsScaffold = () => {
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { const r = await reportsEntApi.history(); setHistory(r.reports || []); }
    catch (e) { setError("Sign in to view reports."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to view reports."); }, []);

  const generate = async (kind) => {
    setBusy(true);
    try { await reportsEntApi.generate(kind); await refresh(); }
    catch (e) { setError("Generation requires admin/compliance/coach role."); }
    finally { setBusy(false); }
  };

  const KINDS = ["team_performance", "injury_trends", "healthiq_risk_summary", "compliance_status", "roster_analytics", "staff_performance"];

  return (
    <EntPageShell title="Reports" testId="reports-scaffold"
      lede="Branded PDF/CSV/Excel exports using AuditIQ font standards (13.5/11.5/10.5/9.5/8 pt). Schedule daily/weekly/monthly delivery via SendGrid.">
      {error && <ErrorMsg message={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="reports-generate-card">
          <div style={panelHeader}>Generate report</div>
          {KINDS.map((k) => (
            <button key={k} data-testid={`reports-generate-${k}`} style={{ ...ghostBtn(), display: "block", width: "100%", marginBottom: 6 }} onClick={() => generate(k)} disabled={busy}>
              {k.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div style={cardStyle} data-testid="reports-history-card">
          <div style={panelHeader}>History ({history.length})</div>
          {history.length === 0 ? <Empty note="Generate a report to populate this log." /> :
            history.map((r) => (
              <div key={r.id} style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "8px 0" }}>
                <div style={{ fontSize: 12 }}>{r.report_type}</div>
                <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)" }}>{fmtDate(r.generated_at)} · by {r.generated_by}</div>
              </div>
            ))}
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §11 Mobile/Offline scaffold
export const MobileOfflineScaffold = () => {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  return (
    <EntPageShell title="Mobile / Offline Mode" testId="mobile-scaffold"
      lede="Sideline-ready responsive layout. Service worker + IndexedDB queue for offline injury reports, attendance, and stat updates. Conflict resolution favors server-of-record.">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="mobile-status-card">
          <div style={panelHeader}>Connection status</div>
          <div data-testid="mobile-online-indicator" style={{ display: "inline-block", padding: "10px 18px", border: `1px solid ${online ? ENT_COLOR.green : ENT_COLOR.amber}`, color: online ? ENT_COLOR.green : ENT_COLOR.amber, borderRadius: 999, fontFamily: "Cinzel, Georgia, serif", fontSize: 12, letterSpacing: 1.4, textTransform: "uppercase" }}>
            {online ? "Online" : "Offline — changes will sync when reconnected"}
          </div>
        </div>
        <div style={cardStyle} data-testid="mobile-capabilities-card">
          <div style={panelHeader}>Offline capabilities</div>
          <ul style={{ listStyle: "none", padding: 0, fontSize: 12, lineHeight: 1.9 }}>
            <li>· Record injury report (queued)</li>
            <li>· Record attendance (queued)</li>
            <li>· Update athlete stats (queued)</li>
            <li>· View last cached schedule, roster, Health IQ</li>
          </ul>
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §12 StaffWorkflows scaffold
export const StaffWorkflowsScaffold = () => {
  const [tasks, setTasks] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try { const [t, o] = await Promise.all([tasksApi.list(), tasksApi.overdue()]); setTasks(t.tasks || []); setOverdue(o.overdue || []); }
    catch (e) { setError("Sign in to view tasks."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to view tasks."); }, []);

  const cols = { todo: [], in_progress: [], pending_approval: [], completed: [] };
  tasks.forEach((t) => { (cols[t.status] || cols.todo).push(t); });

  const advance = async (id, next) => {
    try { await tasksApi.update({ task_id: id, status: next }); await refresh(); }
    catch (e) { setError("Update failed."); }
  };

  return (
    <EntPageShell title="Staff Workflows" testId="workflows-scaffold"
      lede="Kanban for injury clearances, document approvals, compliance actions, and schedule changes. 24-hour overdue reminders via SendGrid + in-app push.">
      {error && <ErrorMsg message={error} />}
      <div style={{ marginBottom: 16, fontSize: 12, color: ENT_COLOR.amber }} data-testid="workflows-overdue-banner">
        {overdue.length} overdue task(s)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }} data-testid="workflows-board">
        {Object.entries(cols).map(([col, items]) => (
          <div key={col} style={cardStyle} data-testid={`workflows-col-${col}`}>
            <div style={panelHeader}>{col.replace(/_/g, " ")} ({items.length})</div>
            {items.length === 0 && <Empty note="—" />}
            {items.map((t) => (
              <div key={t.id} data-testid="workflows-task" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "8px 0" }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{t.title}</div>
                <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)" }}>due {fmtDate(t.due_date)}</div>
                {t.status !== "completed" && (
                  <button style={{ ...ghostBtn(true), marginTop: 6 }}
                    onClick={() => advance(t.id, t.status === "todo" ? "in_progress" : t.status === "in_progress" ? "pending_approval" : "completed")}>
                    Advance →
                  </button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </EntPageShell>
  );
};

// ==================================================== §13 Integrations scaffold
export const IntegrationsScaffold = () => {
  const [conns, setConns] = useState([]);
  const [supported, setSupported] = useState({});
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ system_name: "Banner @ UF", system_type: "banner_ellucian", endpoint_url: "https://banner.uf.edu/api", credentials: "demo-key" });

  const refresh = async () => {
    try { const r = await integrationApi.status(); setConns(r.connections || []); setSupported(r.supported || {}); }
    catch (e) { setError("Sign in (admin) to view integrations."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in to view integrations."); }, []);

  const connect = async () => {
    try { await integrationApi.connect({ ...form, protocol: "rest" }); await refresh(); }
    catch (e) { setError("Connect requires admin role."); }
  };

  const sync = async (id) => { try { await integrationApi.sync(id); await refresh(); } catch (e) { setError("Sync failed."); } };

  return (
    <EntPageShell title="Integration Gateway" testId="integrations-scaffold"
      lede="Banner · PowerSchool · Ellucian Colleague · Catapult Sports · HL7 FHIR. AES-256 credential encryption + Celery-scheduled syncs.">
      {error && <ErrorMsg message={error} />}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
        <div style={cardStyle} data-testid="integrations-list-card">
          <div style={panelHeader}>Connected systems ({conns.length})</div>
          {conns.length === 0 ? <Empty note="No integrations yet — connect one from the right." /> :
            conns.map((c) => (
              <div key={c.id} data-testid="integrations-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)", padding: "10px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13 }}>{c.system_name}</div>
                  <div style={{ fontSize: 10, color: "rgba(244,239,230,0.55)" }}>{c.system_type} · {c.protocol} · last sync {fmtDate(c.last_sync)}</div>
                </div>
                <button data-testid="integrations-sync-btn" style={ghostBtn(true)} onClick={() => sync(c.id)}>Sync</button>
              </div>
            ))}
        </div>
        <div style={cardStyle} data-testid="integrations-connect-card">
          <div style={panelHeader}>Connect new system</div>
          <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>System type</label>
          <select data-testid="integrations-type-select" value={form.system_type} onChange={(e) => setForm({ ...form, system_type: e.target.value })}
            style={{ width: "100%", padding: 8, marginTop: 4, marginBottom: 10, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }}>
            {Object.entries(supported).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {["system_name", "endpoint_url", "credentials"].map((k) => (
            <div key={k} style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 10, color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1 }}>{k}</label>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                style={{ width: "100%", padding: 8, marginTop: 4, background: "#0d1f3c", border: `1px solid ${ENT_COLOR.gold}`, color: ENT_COLOR.text, borderRadius: 4 }} />
            </div>
          ))}
          <button data-testid="integrations-connect-btn" style={{ ...goldBtn(), marginTop: 6 }} onClick={connect}>Connect</button>
        </div>
      </div>
    </EntPageShell>
  );
};

// ==================================================== §14 AuditViewer scaffold
export const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({});

  const refresh = async (f = {}) => {
    try { const r = await auditEntApi.logs(f); setLogs(r.logs || []); }
    catch (e) { setError("Audit log access requires admin/auditor role."); }
  };
  useEffect(() => { if (auth.token()) refresh(); else setError("Sign in (auditor/admin) to view audit logs."); }, []);

  return (
    <EntPageShell title="Audit Log Viewer" testId="audit-viewer"
      lede="HIPAA + FERPA audit trails. 6-year retention for HIPAA-flagged events; 5-year retention for FERPA-flagged events. CSV export available.">
      {error && <ErrorMsg message={error} />}
      <div style={cardStyle} data-testid="audit-filters-card">
        <div style={panelHeader}>
          <span>Filters</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button data-testid="audit-filter-all"   style={ghostBtn(true)} onClick={() => { setFilter({}); refresh({}); }}>All</button>
            <button data-testid="audit-filter-hipaa" style={ghostBtn(true)} onClick={() => { setFilter({ hipaa_only: true }); refresh({ hipaa_only: true }); }}>HIPAA only</button>
            <button data-testid="audit-filter-ferpa" style={ghostBtn(true)} onClick={() => { setFilter({ ferpa_only: true }); refresh({ ferpa_only: true }); }}>FERPA only</button>
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="audit-table" style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: ENT_COLOR.gold, fontFamily: "Cinzel, Georgia, serif", letterSpacing: 1.2, textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>Timestamp</th>
                <th style={{ padding: "6px 8px" }}>User</th>
                <th style={{ padding: "6px 8px" }}>Action</th>
                <th style={{ padding: "6px 8px" }}>Resource</th>
                <th style={{ padding: "6px 8px" }}>Flags</th>
                <th style={{ padding: "6px 8px" }}>Retention</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={6}><Empty note="No audit events match this filter." testid="audit-empty" /></td></tr>
              )}
              {logs.slice(0, 100).map((l) => (
                <tr key={l.id} data-testid="audit-row" style={{ borderTop: "1px solid rgba(201,168,76,0.18)" }}>
                  <td style={{ padding: "6px 8px" }}>{fmtDate(l.timestamp)}</td>
                  <td style={{ padding: "6px 8px" }}>{l.user_id}</td>
                  <td style={{ padding: "6px 8px" }}>{l.action}</td>
                  <td style={{ padding: "6px 8px" }}>{l.resource_type}/{l.resource_id?.slice?.(0, 8)}</td>
                  <td style={{ padding: "6px 8px" }}>
                    {l.hipaa_flag && <span style={{ color: ENT_COLOR.purple, marginRight: 6, fontSize: 9 }}>HIPAA</span>}
                    {l.ferpa_flag && <span style={{ color: ENT_COLOR.amber, fontSize: 9 }}>FERPA</span>}
                  </td>
                  <td style={{ padding: "6px 8px", fontSize: 9, color: "rgba(244,239,230,0.55)" }}>{l.retention_until ? new Date(l.retention_until).getFullYear() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </EntPageShell>
  );
};
